import get from 'lodash/get';
import isNil from 'lodash/isNil';
import uuidV4 from 'uuid/v4';
import {window} from 'ssr-window';
import optimizely from '@optimizely/optimizely-sdk';
import defaultLogger from '@optimizely/optimizely-sdk/lib/plugins/logger';
import {LOG_LEVEL} from '@optimizely/optimizely-sdk/lib/utils/enums';
import Arg from '@spothero/arg';
import APIUtils from '@spothero/utils/api';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import SegmentUtils from 'utils/segment';
import GTMUtils from 'utils/gtm';
import SentryUtils from '@spothero/utils/sentry';
import Config from 'config';

export const IMMEDIATE_FEATURES = {
    WEB_CANADA: 'web_canada',
    MW_RENO_CHECKOUT: 'mw_reno_checkout',
    FILTER_SORT: 'filter_sort'
};
// Define variables by feature in the format `[variableName, type]`
export const VARIABLES = {
    /* eslint-disable camelcase */
    filter_sort: [
        ['sort_distance', 'Boolean'],
        ['sort_price', 'Boolean'],
        ['filters', 'Boolean'],
        ['map', 'Boolean'],
    ],
    mw_reno_checkout: [
        ['headers_footers', 'Boolean'],
    ],
    /* eslint-enable camelcase */
};
export const IMMEDIATE_AB_EXPERIMENTS = {
    MAP_SPOT_DETAILS: 'map_spot_details',
    EVENTS_MODAL: 'events_modal_cvr_effect',
    AA_TEST: 'aa_test',
    MW_AA_TEST: 'mw_aa_test'
};

const SUPPORTED_TYPES = ['Boolean', 'Double', 'Integer', 'String'];

/**
 * Utilities for working with Optimizely.
 * @module ExperimentUtils
 */
const ExperimentUtils = {
    client: null,

    forceVariation: null,

    logLevel: 'ERROR',

    userAttributes: {},

    // experiment and variation ids for reporting to Segment
    segmentExperiments: [],

    init(onLoad) {
        if (Config.isBot || this.client) {
            if (onLoad) {
                // Ensure onLoads are initialized
                setTimeout(() => {
                    onLoad();
                }, 0);
            }
        } else {
            APIUtils
                .get('/experiments/optimizely-datafile/')
                .then(response => {
                    if (get(window, 'location.search', false)) {
                        const {
                            optimizely_x: forceVariation, // e.g. whitelist IDs
                            optimizely_log: logLevel, // e.g. INFO, DEBUG, ERROR
                        } = Arg.parse(window.location.search);

                        if (forceVariation) {
                            this.forceVariation = forceVariation;
                        }

                        if (logLevel) {
                            this.logLevel = logLevel;
                        }
                    }

                    this.client = optimizely.createInstance({
                        datafile: response.data,
                        skipJSONValidation: true,
                        logger: defaultLogger.createLogger({
                            logLevel: LOG_LEVEL[this.logLevel]
                        }),
                    });

                    // https://segment.com/docs/destinations/optimizely-web/#optimizely-full-stack-javascript
                    // See section titled Optimizely Full Stack (JavaScript) - Getting Started
                    // This is required for Optimizely/Segment integration
                    window.optimizelyClientInstance = this.client;

                    this.initUserAttributes();

                    this.clearVariationsCookie();
                })
                .catch(error => {
                    SentryUtils.sendMessage(error);

                    this.client = null;
                })
                .then(() => {
                    if (onLoad) {
                        onLoad();
                    }
                });
        }
    },

    /**
     * Checks Optimizely to see if the user is bucketed in this experiment
     * Activates the experiment for the current session; an impression will be sent to Optimizely
     * Appends the experiment name and variation name to a cookie (used by analytics and the Reservations API)
     * @static
     * @function activate
     * @param {String} experimentName - Experiment name
     * @param {Object} options - Options to use
     * @param {Boolean} options.sendToAnalytics - Whether or not to send experiment information to Segment and GTM (defaults to true)
     * @example ExperimentUtils.activate('mw_checkout_value_props') => 'vp_below';
     * @example ExperimentUtils.activate('mw_checkout_value_props', {sendToAnalytics: false}) => 'vp_below';
     * @return {String} variationName - For each variation, returns the name of the variation assigned to this user, or 'a'
     */
    activate(experimentName, {sendToAnalytics = true} = {}) {
        if (!Config.isBot && !this.client) {
            throw new Error(`The test client is not initialized. Please call init prior to activate.`);
        }

        if (!experimentName) {
            throw new Error(`Missing required argument "experimentName".`);
        }

        if (!Config.isBot && Boolean(this.client) && !Config.runningTests) {
            const variationName = this.client.activate(experimentName, this.getId(), this.userAttributes) || 'a';

            this.updateVariationsCookie({
                [experimentName]: variationName
            });

            this.updateSegmentExperiments({
                experimentName,
                variationName,
                type: 'ab-test'
            });

            if (sendToAnalytics) {
                this.sendToAnalytics();
            }

            return variationName;
        }

        return 'a';
    },

    /**
     * Checks if a feature is enabled for a user
     * @static
     * @function isFeatureEnabled
     * @param {String} featureName - The name of the feature
     * @param {Object} options - Options to use
     * @param {Boolean} options.sendToAnalytics - Whether or not to send experiment information to Segment and GTM (defaults to true)
     * @example ExperimentUtils.isFeatureEnabled('web_canada');
     * @example ExperimentUtils.isFeatureEnabled('web_canada', {sendToAnalytics: true});
     * @returns {Boolean} isEnabled - Whether or not the feature is enabled for this user
     */
    isFeatureEnabled(featureName, {sendToAnalytics = true} = {}) {
        if (!Config.isBot && !this.client) {
            throw new Error(`The test client is not initialized. Please call init prior to isFeatureEnabled.`);
        }

        if (!featureName) {
            throw new Error(`Missing required argument "featureName".`);
        }

        if (!Config.isBot && Boolean(this.client) && !Config.runningTests) {
            const isFeatureEnabled = this.client.isFeatureEnabled(featureName, this.getId(), this.userAttributes);

            if (isFeatureEnabled) {
                const featureExperiments = this.getExperimentsAndVariationsForFeature(featureName);

                featureExperiments.forEach(experiment => {
                    const {
                        experimentName,
                        variationName
                    } = experiment;

                    this.updateVariationsCookie({
                        [experimentName]: variationName
                    });
        
                    this.updateSegmentExperiments({
                        ...experiment,
                        type: 'feature-test'
                    });
                });

                if (sendToAnalytics) {
                    this.sendToAnalytics();
                }
            }

            return isFeatureEnabled;
        }

        return false;
    },

    /**
     * Checks Optimizely to see if the user is bucketed in this experiment
     * Does not activate the experiment for the current session; no impression will be sent to Optimizely
     * @static
     * @function getVariation
     * @param {String} experimentName - Name of experiment
     * @example ExperimentUtils.getVariation('homepage_tabbed_search') => 'tabbed_with_value_props';
     * @example ExperimentUtils.getVariation('homepage_tabbed_search', 'mw_reno_spot') => {
     *   homepage_tabbed_search: 'tabbed_with_value_props',
     *   mw_reno_spot: 'variation2',
     * };
     * @return {String|Object} variationName - For each variation, returns the name of the variation assigned to this user, or 'a'
     * If a single argument is passed, the variation name is returned as a string
     * If multiple arguments are passed, experiment name/variation name key/value pairs are returned
     */
    getVariation(experimentName) {
        if (!Config.isBot && !this.client) {
            throw new Error(`The test client is not initialized. Please call init prior to getVariation.`);
        }

        if (!experimentName) {
            throw new Error(`Missing required argument "experimentName".`);
        }

        if (!Config.isBot && Boolean(this.client) && !Config.runningTests) {
            return this.client.getVariation(experimentName, this.getId(), this.userAttributes) || 'a';
        }

        return 'a';
    },

    /**
     * @static
     * @function getFeatureVariable
     * @param {Object} requiredParams - All params are required
     * @param {String} requiredParams.featureName - The name of the feature
     * @param {String} requiredParams.variableName - The name of the feature variable
     * @param {String} requiredParams.type - The type of the feature variable
     * @example ExperimentUtils.getFeatureVariable({
     *      featureName: 'web_canada',
     *      variableName: 'search_enabled',
     *      type: 'Boolean',
     * });
     * @returns {Double|Integer|String|Boolean} variableValue - The value of the given variable and type for the given feature
     */
    getFeatureVariable(requiredParams) {
        if (!Config.isBot && !this.client) {
            throw new Error(`The test client is not initialized. Please call init prior to getFeatureVariableBoolean.`);
        }

        const missingArguments = Object.keys(requiredParams)
            .filter(param => !requiredParams[param]);

        if (missingArguments.length) {
            throw new Error(`Missing required argument(s): ${missingArguments.join(', ')}`);
        }

        if (!Config.isBot && Boolean(this.client) && !Config.runningTests) {
            const {
                featureName,
                variableName,
                type,
            } = requiredParams;
            const formattedType = type[0].toUpperCase() + type.substr(1).toLowerCase();

            if (!SUPPORTED_TYPES.find(supportedType => supportedType === formattedType)) {
                throw new Error(`Type must be one of: ${SUPPORTED_TYPES.join(', ')}. Given type: ${type}`);
            }

            return this.client[`getFeatureVariable${formattedType}`](featureName, variableName, this.getId(), this.userAttributes);
        }

        return false;
    },

    /**
     * Get all feature variables that were defined in VARIABLES for the given feature
     * @static
     * @function getEnabledFeatureVariables
     * @param {String} featureName - Name of the enabled feature
     * @example ExperimentUtils.isFeatureEnabled('mw_reno_spot') && ExperimentUtils.getEnabledFeatureVariables('mw_reno_spot');
     * @returns {Object} - Returns dictionary of variable name/value key/value pairs
     */
    getEnabledFeatureVariables(featureName) {
        const variables = VARIABLES[featureName];

        return variables.reduce((allVariables, [variableName, type]) => ({
            ...allVariables,
            [variableName]: this.getFeatureVariable({
                featureName,
                variableName,
                type,
            })
        }), {});
    },

    /**
     * Return experiment names and variations for a given feature
     * @static
     * @function getExperimentsAndVariationsForFeature
     * @param {String} featureName - Name of feature flag
     * @returns {Array} featureExperiments - Array of objects, each containing experiment information
     */
    getExperimentsAndVariationsForFeature(featureName) {
        if (this.client) {
            const {
                configObj: {
                    featureKeyMap,
                    experimentIdMap
                }
            } = this.client;
            const experimentIds = featureKeyMap[featureName].experimentIds;

            return experimentIds.reduce((featureExperiments, experimentId) => {
                const experimentName = experimentIdMap[experimentId].key;
                const variationName = this.getVariation(experimentName);

                featureExperiments.push({
                    experimentName,
                    variationName
                });

                return featureExperiments;
            }, []);
        } else {
            return [];
        }
    },

    /**
     * Return the value of the sh-experiment-id cookie (or create new cookie and return its value)
     * @static
     * @function getIdCookie
     * @returns {String} - Either the existing cookie or a new value from a new cookie
     */
    getIdCookie() {
        const shExperimentCookie = StorageUtils.get('sh-experiment-id', 'cookie');

        return (!isNil(shExperimentCookie))
            ? shExperimentCookie.toString()
            : this.createIdCookie();
    },

    /**
     * Create a unique ID and store it in the sh-experiment-id cookie
     * @static
     * @function createIdCookie
     * @param {String} id - An ID to set as the sh-experiment-id cookie
     * @returns {String} uuid - Creates a unique ID and stories it as the sh-experiment-id cookie
     */
    createIdCookie(id) {
        const uuid = id || uuidV4();

        StorageUtils.set('sh-experiment-id', uuid, 'cookie', 365);

        return uuid;
    },

    /**
     * Return user id for use with Optimizely SDK
     * @static
     * @function getId
     * @returns {String} sh-experiment-id - Either the cookie value or the value passed to the optimizely_x query param
     */
    getId() {
        return this.forceVariation || this.getIdCookie();
    },

    /**
     * Return the experiment names and variations stored in sh-experiment-variations cookie
     * @static
     * @function getVariationsCookie
     * @returns {Object} sh-experiment-variations - keys are experiment names and corresponding values are variation names.
     */
    getVariationsCookie() {
        return StorageUtils.get('sh-experiment-variations', 'cookie') || {};
    },

    /**
     * Update the experiment names and variations stored in sh-experiment-variations cookie
     * @static
     * @function updateVariationsCookie
     * @param {Object} newVariations - New object where key is experiment name corresponding value is variation name
     * @example updateVariationsCookie({'mw_checkout_value_props': 'vp_below'});
     * @returns {void}
     */
    updateVariationsCookie(newVariations) {
        const currentVariations = this.getVariationsCookie();
        const updatedVariations = {
            ...currentVariations,
            ...newVariations
        };

        StorageUtils.set('sh-experiment-variations', updatedVariations, 'cookie', 365);
    },

    /**
     * Clear the experiment names and variations stored in sh-experiment-variations cookie
     * @static
     * @function clearVariationsCookie
     * @returns {void}
     */
    clearVariationsCookie() {
        StorageUtils.set('sh-experiment-variations', {}, 'cookie', 365);
    },

    /**
     * Returns experiment data formatted specifically for Segment
     * @static
     * @function getSegmentExperiments
     * @returns {Array} - Array with object for each active experiment
     */
    getSegmentExperiments() {
        return this.segmentExperiments;
    },

    /**
     * Update experiment data formatted specifically for Segment
     * @static
     * @function getSegmentExperiments
     * @param {String} experimentName - Name of experiment
     * @param {String} variationName - Name of experiment variation
     * @param {String} type - 'ab-test' | 'feature-test'
     * @returns {void}
     */
    updateSegmentExperiments({experimentName, variationName, type} = {}) {
        if (this.client) {
            const {
                configObj: {
                    experimentKeyMap
                }
            } = this.client;

            if (variationName !== 'a') {
                const variationObj = experimentKeyMap[experimentName].variationKeyMap[variationName];

                /* eslint-disable camelcase */
                this.segmentExperiments.push({
                    experiment_name: experimentName,
                    experiment_id: experimentKeyMap[experimentName].id,
                    variation_name: variationName,
                    variation_id: variationObj.id,
                    type
                });
                /* eslint-enable camelcase */
            }
        }
    },

    /**
     * Sends experiment data to Segment and GTM
     * @static
     * @function sendToAnalytics
     * @returns {void}
     */
    sendToAnalytics() {
        if (Config.isDeployed) {
            SegmentUtils.addTraits({
                experiments: this.getSegmentExperiments()
            });
        }

        GTMUtils.push({
            event: 'experimentVariationsUpdated',
            experimentVariations: JSON.stringify(this.getVariationsCookie())
        });
    },

    /**
     * Initializes user attributes with device and environment data
     * @static
     * @function initUserAttributes
     * @returns {void}
     */
    initUserAttributes() {
        const userAttributes = {
            client: (EnvironmentUtils.isMobileDevice())
                ? 'mobile-web'
                : 'web',
            environment: (Config.isProduction)
                ? 'production'
                : 'staging'
        };

        if (Config.requestIP) {
            userAttributes.ip = Config.requestIP;
        }

        this.userAttributes = userAttributes;
    },

    /**
     * Add new, relevant properties to be sent along with requests for feature and experiment values
     * @static
     * @function setUserAttributes
     * @param {Object} userAttributes - Supported attributes are defined in our Optimizely implementation
     * @returns {void}
     */
    setUserAttributes(userAttributes) {
        this.userAttributes = {
            ...this.userAttributes,
            ...userAttributes,
        };
    },

    /**
     * A single location to bucket experiments and features needed on app load
     * Formats results for retrieval with redux (store.user)
     * @static
     * @function getBuckets
     * @param {Object} experimentObj - Dictionary of constant and Optimizely experiment name key/value pairs
     * @param {Object} featuresObj - Dictionary of constant and Optimizely feature flag name key/value pairs
     * @returns {Object} - Formatted experiment values for injecting into the redux (store.user)
     */
    getBuckets(experimentObj = IMMEDIATE_AB_EXPERIMENTS, featuresObj = IMMEDIATE_FEATURES) {
        if (!this.client) {
            SentryUtils.sendMessage({
                message: 'The Optimizely client was not initialized prior to calling getBuckets.',
                level: 'error'
            });

            return {};
        }

        const experiments = Object.keys(experimentObj)
            .map(experimentName => experimentObj[experimentName])
            .reduce((variations, experimentName) => ({
                ...variations,
                [experimentName]: this.activate(experimentName, {sendToAnalytics: false})
            }), {});
        const features = Object.keys(featuresObj)
            .map(featureName => featuresObj[featureName])
            .reduce((enabledFeatures, featureName) => {
                const variables = VARIABLES[featureName];
                const isEnabled = this.isFeatureEnabled(featureName, {sendToAnalytics: false});

                return {
                    ...enabledFeatures,
                    [featureName]: (variables)
                        ? this.getEnabledFeatureVariables(featureName)
                        : isEnabled
                };
            }, {});

        return {
            ...experiments,
            ...features,
        };
    }
};

export default ExperimentUtils;
